// This implementation is heavily inspired by react-aria's implementation
import * as React from 'react'
import type { PropsWithChildren } from 'react'

type IdContextValue = {
  current: number
}

const defaultIdContext: IdContextValue = {
  current: 1
}

const IdContext = React.createContext<IdContextValue>(defaultIdContext)

export const IdProvider = React.memo((props: PropsWithChildren) => {
  return React.createElement(
    IdContext.Provider,
    { value: { current: 1 } },
    props.children
  )
})

const genId = (context: IdContextValue) => context.current++

export function useId(idProp?: string, prefix?: string): string {
  const context = React.useContext(IdContext)
  const [id, setId] = React.useState(context.current)

  React.useEffect(() => {
    setId(genId(context))
  }, [context])

  return React.useMemo(
    () => idProp || [prefix, id].filter(Boolean).join('-'),
    [idProp, prefix, id]
  )
}

export function useIds(idProp?: string, ...prefixes: string[]) {
  const id = useId(idProp)
  return React.useMemo(() => {
    return prefixes.map(prefix => `${prefix}-${id}`)
  }, [id, prefixes])
}
